/**
 * v4 File chat — "Ask about this file" scoped chat. Top of screen shows the
 * file you're talking about; below is a chat thread that ONLY knows about
 * this document. Suggested questions are file-type-aware.
 */

import { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, User, Sparkles, ArrowUpRight, Download, FileText, Mail, Landmark, BarChart3, Receipt as ReceiptIcon } from 'lucide-react';
import { useV4 } from './i18n';
import { FILES } from './FilesPage';
import { cn } from '@/lib/utils';

interface Msg { id: number; from: 'user' | 'ai'; text: string; forwardable?: boolean; forwardedToHuman?: boolean }

const SUGGESTED_BY_CATEGORY: Record<string, string[]> = {
  tax: [
    'What was my taxable income?',
    'How much tax did I pay?',
    'What deductions did we claim?',
    'When is the next instalment due?',
  ],
  bas: [
    'What\'s the total GST on this BAS?',
    'When does this need to be lodged?',
    'Has this been paid?',
  ],
  financials: [
    'What was my profit?',
    'How did expenses change from last period?',
    'Show me my biggest expense categories',
  ],
  letters: [
    'What does this letter mean?',
    'What do I need to do?',
    'When is the deadline?',
  ],
  receipts: [
    'Is this a business expense?',
    'What category should this be?',
  ],
  other: [
    'Summarise this document for me',
    'What are the key dates I need to know?',
  ],
};

const CAT_ICON: Record<string, any> = {
  tax: Landmark, bas: FileText, financials: BarChart3,
  letters: Mail, receipts: ReceiptIcon, other: FileText,
};

const CAT_COLOR: Record<string, { bg: string; text: string }> = {
  tax:        { bg: 'bg-rose-50 dark:bg-rose-950/40',    text: 'text-rose-700 dark:text-rose-300' },
  bas:        { bg: 'bg-amber-50 dark:bg-amber-950/40',   text: 'text-amber-700 dark:text-amber-300' },
  financials: { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-300' },
  letters:    { bg: 'bg-[var(--v4-accent-tint)]',  text: 'text-[var(--v4-accent-text)]' },
  receipts:   { bg: 'bg-teal-50 dark:bg-teal-950/40',    text: 'text-teal-700' },
  other:      { bg: 'bg-muted',  text: 'text-foreground/80' },
};

export default function FileChatPageV4() {
  const { t, dir } = useV4();
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();
  const file = useMemo(() => FILES.find(f => f.id === fileId), [fileId]);

  const [messages, setMessages] = useState<Msg[]>([
    { id: 1, from: 'ai', text: file ? `Hi! I have read "${file.filename}". Ask me anything about it.` : 'File not found.' },
  ]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);

  if (!file) {
    return (
      <div className="space-y-3 pt-2">
        <Link to="/client-pwa-v4/files" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t('back_to_files')}
        </Link>
        <p className="text-sm text-muted-foreground">File not found.</p>
      </div>
    );
  }

  const Icon = CAT_ICON[file.category];
  const color = CAT_COLOR[file.category];
  const suggested = SUGGESTED_BY_CATEGORY[file.category] || SUGGESTED_BY_CATEGORY.other;

  const send = (txt: string) => {
    const text = txt.trim();
    if (!text) return;
    setMessages(m => [...m, { id: Date.now(), from: 'user', text }]);
    setInput('');
    setThinking(true);
    setTimeout(() => {
      setMessages(m => [...m, {
        id: Date.now() + 1,
        from: 'ai',
        text: cannedFileAnswer(file, text),
        forwardable: true,
      }]);
      setThinking(false);
    }, 900);
  };

  const forward = (id: number) => {
    setMessages(m => m.map(msg => msg.id === id ? { ...msg, forwardedToHuman: true, forwardable: false } : msg));
    setMessages(m => [...m, { id: Date.now(), from: 'ai', text: t('forwarded_to_accountant') }]);
  };

  return (
    <div className="flex h-[calc(100vh-160px)] flex-col">
      {/* File header */}
      <div className="pb-3 pt-1">
        <button onClick={() => navigate(-1)} className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground">
          <ArrowLeft className={`h-3.5 w-3.5 ${dir === 'rtl' ? 'rotate-180' : ''}`} />
          {t('back_to_files')}
        </button>
        <div className="rounded-2xl border border-border bg-card p-3">
          <div className="flex items-start gap-3">
            <div className={cn('flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl', color.bg, color.text)}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{file.filename}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                FY {file.fy} · {file.date} · {t('pages_count', { n: file.pages })}
                {file.preparedBy ? ` · ${t('prepared_by')} ${file.preparedBy}` : ''}
              </p>
              <div className="mt-2">
                <button className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground/80 hover:border-muted-foreground/40">
                  <Download className="h-3 w-3" />
                  {t('download')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 pb-32">
        {messages.map(msg => (
          <div key={msg.id} className={cn('flex gap-2', msg.from === 'user' ? 'justify-end' : 'justify-start')}>
            {msg.from === 'ai' && (
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white">
                <Sparkles className="h-3.5 w-3.5" />
              </div>
            )}
            <div className={cn('flex flex-col gap-1.5', msg.from === 'user' ? 'items-end' : 'items-start')}>
              <div
                className={cn(
                  'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
                  msg.from === 'user'
                    ? 'bg-[var(--v4-accent)] text-white rounded-br-md'
                    : 'bg-card border border-border text-foreground rounded-bl-md',
                  dir === 'rtl' && msg.from === 'user' && 'rounded-bl-md rounded-br-2xl',
                  dir === 'rtl' && msg.from === 'ai' && 'rounded-br-md rounded-bl-2xl'
                )}
              >
                {msg.text}
              </div>
              {msg.forwardable && !msg.forwardedToHuman && (
                <button
                  onClick={() => forward(msg.id)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground/80 transition-colors hover:border-muted-foreground/60 hover:text-foreground"
                >
                  <User className="h-3 w-3" />
                  {t('ask_marcus_instead')}
                  <ArrowUpRight className="h-3 w-3" />
                </button>
              )}
            </div>
            {msg.from === 'user' && (
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted text-foreground/80">
                <User className="h-3.5 w-3.5" />
              </div>
            )}
          </div>
        ))}

        {thinking && (
          <div className="flex gap-2">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white">
              <Sparkles className="h-3.5 w-3.5" />
            </div>
            <div className="flex items-center gap-1 rounded-2xl rounded-bl-md border border-border bg-card px-3.5 py-3">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/50" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/50" style={{ animationDelay: '150ms' }} />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/50" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}

        {messages.length === 1 && (
          <div className="pt-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/60 mb-2">Try asking</p>
            <div className="flex flex-wrap gap-2">
              {suggested.map(q => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="rounded-full border border-border bg-card px-3 py-1.5 text-[12px] text-foreground/80 hover:border-muted-foreground/40 hover:text-foreground transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="fixed bottom-20 left-0 right-0 z-20 mx-auto max-w-[720px] px-4">
        <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-1 shadow-md">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={t('type_question')}
            className="flex-1 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground/60 focus:outline-none"
          />
          <button
            type="submit"
            disabled={!input.trim()}
            aria-label={t('send')}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--v4-accent)] text-white shadow-sm transition-all hover:bg-[var(--v4-accent-hover)] disabled:bg-muted disabled:text-muted-foreground"
          >
            <Send className={cn('h-4 w-4', dir === 'rtl' && '-scale-x-100')} />
          </button>
        </div>
      </form>
    </div>
  );
}

function cannedFileAnswer(file: any, q: string): string {
  const lower = q.toLowerCase();
  const filename = file.filename.toLowerCase();

  if (file.category === 'tax' || filename.includes('tax')) {
    if (lower.includes('income') || lower.includes('taxable')) {
      return 'Your taxable income for FY ' + file.fy + ' was $187,420. After tax of $48,710 your after-tax profit was $138,710.';
    }
    if (lower.includes('deduction') || lower.includes('claim')) {
      return 'Top deductions claimed: motor vehicle ($8,420), home office (1,240 hours × $0.70 = $868), tools & equipment ($4,820), professional development ($1,200).';
    }
    if (lower.includes('paid') || lower.includes('tax')) {
      return 'Tax payable was $48,710. With the PAYG instalments already paid ($42,000), the final balance owing was $6,710. This was paid on the due date.';
    }
  }

  if (file.category === 'bas' || filename.includes('bas')) {
    if (lower.includes('gst') || lower.includes('total')) {
      return 'Total GST owing on this BAS: $4,820 (GST on sales $7,640 − GST credits $2,820). PAYG withholding: $2,400. PAYG instalment: $600. Total payable: $7,820.';
    }
    if (lower.includes('lodge') || lower.includes('due')) {
      return 'Lodgement due 28 May 2026. Marcus has prepared the draft — you will get a notification to approve it.';
    }
    if (lower.includes('paid')) {
      return 'Not yet lodged — this is a draft. Once approved, payment is due the same day as lodgement.';
    }
  }

  if (file.category === 'financials' || filename.includes('balance') || filename.includes('p&l')) {
    if (lower.includes('profit') || lower.includes('net')) {
      return 'Net profit for the period was $5,370 — that\'s 25.3% of revenue. Up 8% vs the prior period.';
    }
    if (lower.includes('expense') || lower.includes('biggest')) {
      return 'Biggest expense categories: Materials ($8,920 — 56%), Rent ($2,800 — 18%), Wages ($2,100 — 13%), Subcontractor ($1,240 — 8%).';
    }
  }

  if (file.category === 'letters') {
    if (lower.includes('mean') || lower.includes('summary')) {
      return 'In plain language: ' + file.filename.replace(/\.pdf$/, '') + ' is a routine notification. No urgent action required.';
    }
    if (lower.includes('do') || lower.includes('action')) {
      return 'No action needed from you. The deadline is handled automatically by Marcus.';
    }
    if (lower.includes('deadline') || lower.includes('when')) {
      return 'The relevant deadline mentioned in this letter is in 12 days. Your accountant is already on top of it.';
    }
  }

  return 'I have read this file but I am not sure about that specific question. Tap "Ask Marcus instead" and your accountant will look at it.';
}
